/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useIdle from 'hooks/use_Idle_Timer';
import { RootReducerState } from 'redux/reducers';
import { connect, useSelector } from 'react-redux';
import { mmurApi } from 'utility/axios_config';
import CurrentUserDto from 'generated/Result/Security/Models/CurrentUserDto';
import CategoryResult from 'generated/Result/CategoryClientContent/CategoryResult';
import { millisToMinutesAndSeconds } from 'utility/helpers';
import { DEFAULT_TIMEOUT, DEFAULT_WARNING_TIMEOUT } from 'utility/constants';
import content from 'utility/token_helper';
import { securityEndpoints } from 'endpoints';
import { DisplayModal } from '../form/display_modal';

interface HandleSessionTimeoutProps {
  categoryClientContent: CategoryResult[],
  currentUser: CurrentUserDto,
  wapiRefetch: boolean
}

function HandleSessionTimeout(props: HandleSessionTimeoutProps) {
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const hasTriggeredLogout = useRef(false);

  // Refs to avoid stale closures in idle/prompt callbacks
  const showSessionModalRef = useRef(false);
  const messageRef = useRef<(data: any, broadcast?: boolean) => void>(null!);
  const resetRef = useRef<() => void>(null!);

  const sessionTimeout = useSelector((state: RootReducerState) => state.global.sessionTimeoutInSeconds);
  const sessionWarningTimeout = useSelector((state: RootReducerState) => state.global.sessionWarningTimeoutInSeconds);
  const warningTimeout = sessionWarningTimeout != null ? sessionWarningTimeout : DEFAULT_WARNING_TIMEOUT;
  const timeout = sessionTimeout != null ? sessionTimeout : DEFAULT_TIMEOUT;
  const toBeDisabled = !props.currentUser || !sessionTimeout;
  const history = useHistory();

  // Keep modal ref in sync with state so callbacks are never stale
  useEffect(() => {
    showSessionModalRef.current = showSessionModal;
  }, [showSessionModal]);

  const logout = () => {
    if (hasTriggeredLogout.current) {
      return;
    }
    hasTriggeredLogout.current = true;
    setShowSessionModal(false);
    showSessionModalRef.current = false;
    history.push('/logout?sessionExpired=1');
  };

  // Use ref so useIdleTimer always calls the current version
  const handleIdle = () => {
    if (showSessionModalRef.current) {
      messageRef.current?.({ action: 'LOGOUT_USER' }, true);
    }
  };

  const handlePrompt = () => {
    setShowSessionModal(true);
    showSessionModalRef.current = true;
  };

  const onMessage = (data) => {
    switch (data.action) {
      case 'LOGOUT_USER':
        logout();
        break;
      case 'EXTEND_LOGIN':
        setShowSessionModal(false);
        showSessionModalRef.current = false;
        setRemainingTime(0);
        hasTriggeredLogout.current = false; // ← Reset guard so future logouts work
        // Small delay ensures timer is fully in idle state before reset
        // This prevents syncTimers race condition in crossTab mode
        setTimeout(() => {
          resetRef.current?.();
        }, 50);
        const url = securityEndpoints.keepAlive();
        mmurApi.get(url).then((response) => {
          if (response.status !== 200) {
            logout();
          }
        });
        break;
      default:
        break;
    }
  };

  const {
    getRemainingTime, message, reset
  } = useIdle({
    onIdle: handleIdle,
    onPrompt: handlePrompt,
    idleTime: timeout,
    promptTime: warningTimeout,
    toBeDisabled,
    onMessage
  });

  // Keep refs to latest stable functions from useIdleTimer
  useEffect(() => {
    messageRef.current = message;
    resetRef.current = reset;
  }, [message, reset]);

  // countdown interval
  useEffect(() => {
    // eslint-disable-next-line prefer-const
    let interval = setInterval(() => {
      setRemainingTime(Math.ceil(getRemainingTime() / 1000));
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [getRemainingTime]);

  // Start the timer on initial load and any time it becomes enabled.
  useEffect(() => {
    if (toBeDisabled) {
      return;
    }

    reset();
    setRemainingTime(Math.ceil(getRemainingTime() / 1000));
  }, [props.wapiRefetch, toBeDisabled, getRemainingTime]);

  const handleLogOut = () => {
    if (hasTriggeredLogout.current) {
      return;
    }
    message({ action: 'LOGOUT_USER' }, true);
  };

  const handleStayLoggedIn = () => {
    message({ action: 'EXTEND_LOGIN' }, true);
  };

  return (
    <DisplayModal
      show={showSessionModal}
      buttontext="Extend Session"
      buttonCloseText="Logout"
      heading="Session Timeout Warning"
      data={content('global', 'session-timeout-message', props.categoryClientContent).replace('{remaining time}', millisToMinutesAndSeconds(remainingTime * 1000))}
      label="Session Timeout Warning"
      onHide={handleLogOut}
      confirmAction={handleStayLoggedIn}
      centered
      static="static"
      showXCloseButton={false}
      animation={false}
    />
  );
}

function mapStateToProps(state: RootReducerState) {
  return {
    categoryClientContent: state.content.categoryClientContent,
    currentUser: state.security.user,
    wapiRefetch: state.global.wapiRefetch
  };
}

export default connect(mapStateToProps, null)(HandleSessionTimeout);
